import { useState, useEffect, useCallback, useRef } from 'react'
import { authService } from '../lib/authService'

// Default poll interval: 60 seconds
// Balanced to catch expiry before user acts while minimizing server load
const DEFAULT_POLL_INTERVAL_MS = 60000

export interface SessionHealthState {
  /** Whether the session is currently valid */
  isValid: boolean
  /** Whether we're currently checking session health */
  isChecking: boolean
  /** Last time we successfully verified the session */
  lastCheckedAt: Date | null
  /** Error message if health check fails for non-auth reasons */
  error: string | null
}

export interface UseSessionHealthOptions {
  /** Polling interval in milliseconds (default: 60000) */
  pollIntervalMs?: number
  /** Whether to start polling immediately (default: true) */
  enabled?: boolean
  /** Callback when session expires - useful for showing UI before redirect */
  onSessionExpired?: () => void
  /** Callback to get redirect context (view/songId) when session expires */
  getRedirectContext?: () => { view?: 'list' | 'editor'; songId?: string | null }
}

/**
 * Proactive session health monitoring hook.
 *
 * Polls the backend periodically to detect session expiry BEFORE the user
 * attempts an action that fails. This prevents the "edit for 10 minutes,
 * lose everything on save" scenario.
 *
 * When session expires:
 * 1. Sets isValid to false
 * 2. Calls onSessionExpired callback (if provided)
 * 3. Triggers authService.handleSessionExpired()
 */
export function useSessionHealth(options: UseSessionHealthOptions = {}): SessionHealthState {
  const {
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    enabled = true,
    onSessionExpired,
    getRedirectContext,
  } = options

  const [state, setState] = useState<SessionHealthState>({
    isValid: true,
    isChecking: false,
    lastCheckedAt: null,
    error: null,
  })

  // Refs to avoid stale closures in interval callback
  const onSessionExpiredRef = useRef(onSessionExpired)
  const getRedirectContextRef = useRef(getRedirectContext)
  const sessionExpiredRef = useRef(false)
  const wasLoggedInRef = useRef(false)  // Track if user was ever logged in

  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired
    getRedirectContextRef.current = getRedirectContext
  }, [onSessionExpired, getRedirectContext])

  const checkSessionHealth = useCallback(async () => {
    // Don't check if already expired - prevents duplicate handling
    if (sessionExpiredRef.current) return

    const { data: { session } } = await authService.getSession()

    // If user had a session before but now doesn't, session was cleared elsewhere
    // (e.g., by backendApi.ts when refresh failed)
    if (wasLoggedInRef.current && !session?.access_token) {
      sessionExpiredRef.current = true

      setState(prev => ({
        ...prev,
        isValid: false,
        isChecking: false,
        error: 'Session expired',
      }))

      // Call the callback (for UI updates before redirect)
      if (onSessionExpiredRef.current) {
        onSessionExpiredRef.current()
      }

      // Trigger redirect with context from caller
      const redirectContext = getRedirectContextRef.current?.() || {}
      await authService.handleSessionExpired(redirectContext)
      return
    }

    // Track that user is logged in
    if (session?.access_token) {
      wasLoggedInRef.current = true
    } else {
      // No session and never was logged in - just don't poll
      return
    }

    setState(prev => ({ ...prev, isChecking: true }))

    try {
      // Use /api/auth/user - lightweight endpoint that validates the JWT
      // Returns 401 if token is expired/invalid
      const response = await fetch('/api/auth/user', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      })

      if (response.status === 401 || response.status === 403) {
        // Session expired!
        sessionExpiredRef.current = true

        setState(prev => ({
          ...prev,
          isValid: false,
          isChecking: false,
          error: 'Session expired',
        }))

        // Call the callback first (for UI updates before redirect)
        if (onSessionExpiredRef.current) {
          onSessionExpiredRef.current()
        }

        // Then trigger the standard session expired flow with context from caller
        const redirectContext = getRedirectContextRef.current?.() || {}
        await authService.handleSessionExpired(redirectContext)
        return
      }

      // Session is valid
      setState({
        isValid: true,
        isChecking: false,
        lastCheckedAt: new Date(),
        error: null,
      })

    } catch (error) {
      // Network error - don't treat as session expired, just log
      // User might be offline temporarily
      console.warn('Session health check failed (network):', error)
      setState(prev => ({
        ...prev,
        isChecking: false,
        error: error instanceof Error ? error.message : 'Health check failed',
      }))
    }
  }, [])

  // Set up polling interval
  useEffect(() => {
    if (!enabled) return

    // Check immediately on mount
    checkSessionHealth()

    // Then poll at interval
    const intervalId = setInterval(checkSessionHealth, pollIntervalMs)

    return () => {
      clearInterval(intervalId)
    }
  }, [enabled, pollIntervalMs, checkSessionHealth])

  // Also check on visibility change (user returns to tab)
  useEffect(() => {
    if (!enabled) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User came back to the tab - check session immediately
        checkSessionHealth()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, checkSessionHealth])

  return state
}
